// <s-service-locator> — Service Locations map. Loads Google Maps with the theme's API key,
// drops a numbered pin for each [data-locator-item] (looking up addresses that have no saved
// coordinates), and on search geocodes the shopper's address or ZIP, then re-sorts the list
// nearest-first with distances in miles. One component per file; DOM work scoped to connectedCallback.
class SServiceLocator extends HTMLElement {
  #ac = null;
  #map = null;
  #info = null;
  #geocoder = null;
  #origin = null;
  #entries = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const key = this.dataset.apiKey;
    this.#entries = [...this.querySelectorAll("[data-locator-item]")].map((item) => ({ item, marker: null, position: null }));

    this.querySelector("[data-locator-form]")?.addEventListener("submit", (event) => {
      event.preventDefault();
      const query = new FormData(event.currentTarget).get("q")?.toString().trim();
      if (query) this.#search(query);
    }, { signal: this.#ac.signal });

    this.#entries.forEach((entry) => {
      entry.item.querySelector("[data-locator-focus]")?.addEventListener("click", () => this.#open(entry), { signal: this.#ac.signal });
    });

    // Google calls gm_authFailure when the key rejects this website or API; surface it instead of a blank map.
    window.gm_authFailure = () => this.#showError();
    if (key) this.#load(key).then(() => this.#init()).catch((error) => this.#showError(error));
  }

  #showError(error) {
    if (error) console.error("Service locations map failed to load", error);
    this.querySelector("[data-locator-coming-soon]")?.classList.remove("hidden");
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #load(key) {
    if (window.google?.maps?.importLibrary) return Promise.resolve();
    // Google's callback fires only once importLibrary exists; the script load event can fire earlier.
    window.sServiceLocatorMaps ??= new Promise((resolve, reject) => {
      window.sServiceLocatorMapsReady = () => resolve();
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&v=weekly&loading=async&callback=sServiceLocatorMapsReady`;
      script.async = true;
      script.dataset.googleMaps = "";
      script.addEventListener("error", reject, { once: true });
      document.head.append(script);
    });
    return window.sServiceLocatorMaps;
  }

  async #init() {
    const { Map, InfoWindow } = await google.maps.importLibrary("maps");
    const { Geocoder } = await google.maps.importLibrary("geocoding");
    const { Marker } = await google.maps.importLibrary("marker");
    this.#geocoder = new Geocoder();
    this.#info = new InfoWindow();
    this.#map = new Map(this.querySelector("[data-locator-map]"), {
      center: { lat: 32.8, lng: -117.1 },
      zoom: 9,
      mapTypeControl: true,
      streetViewControl: true,
    });

    await Promise.all(this.#entries.map(async (entry, index) => {
      const { lat, lng, address } = entry.item.dataset;
      // Saved coordinates outside the real latitude/longitude range fall back to the address so one bad entry can't zoom the map out to the whole world.
      const saved = lat && lng ? { lat: Number(lat), lng: Number(lng) } : null;
      const valid = saved && Math.abs(saved.lat) <= 90 && Math.abs(saved.lng) <= 180;
      entry.position = valid ? saved : await this.#geocode(address);
      if (!entry.position) return;
      entry.marker = new Marker({ map: this.#map, position: entry.position, title: entry.item.dataset.name, label: { text: String(index + 1), color: "#fff", fontSize: "11px", fontWeight: "700" } });
      entry.marker.addListener("click", () => this.#open(entry));
    }));
    this.#fit(this.#entries);
  }

  async #geocode(address) {
    try {
      const { results } = await this.#geocoder.geocode({ address, region: "us" });
      const location = results[0]?.geometry?.location;
      return location ? { lat: location.lat(), lng: location.lng() } : null;
    } catch {
      return null;
    }
  }

  async #search(query) {
    const message = this.querySelector("[data-locator-message]");
    message.classList.add("hidden");
    if (!this.#geocoder) return;
    const origin = await this.#geocode(query);
    if (!origin) {
      message.textContent = this.dataset.notFound;
      message.classList.remove("hidden");
      return;
    }
    const { Marker } = await google.maps.importLibrary("marker");
    this.#origin?.setMap(null);
    this.#origin = new Marker({ map: this.#map, position: origin, title: this.dataset.youLabel, icon: { path: google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#1f3a5f", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 } });

    const list = this.querySelector("[data-locator-list]");
    this.#entries.forEach((entry) => { entry.distance = entry.position ? this.#miles(origin, entry.position) : Infinity; });
    this.#entries.sort((a, b) => a.distance - b.distance);
    this.#entries.forEach((entry, index) => {
      list?.append(entry.item);
      entry.item.querySelector("[data-locator-number]").textContent = index + 1;
      entry.marker?.setLabel({ text: String(index + 1), color: "#fff", fontSize: "11px", fontWeight: "700" });
      const distance = entry.item.querySelector("[data-locator-distance]");
      if (Number.isFinite(entry.distance)) {
        distance.textContent = `${entry.distance.toFixed(2)} ${this.dataset.milesLabel}`;
        distance.classList.remove("hidden");
        distance.classList.add("block");
      }
    });
    if (list) list.scrollTop = 0;
    // Zoom to centers within the theme's search radius; if none are that close, include only the nearest one.
    const radius = Number(this.dataset.searchRadius) || 25;
    const nearby = this.#entries.filter((entry) => entry.distance <= radius);
    this.#fit(nearby.length ? nearby : this.#entries.slice(0, 1), origin, 13);
  }

  #fit(entries, extra, maxZoom) {
    const points = entries.map((entry) => entry.position).filter(Boolean);
    if (extra) points.push(extra);
    if (!points.length) return;
    if (points.length === 1) {
      this.#map.setCenter(points[0]);
      this.#map.setZoom(12);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    this.#map.fitBounds(bounds, 40);
    // Keep a single nearby center from zooming in to street level.
    if (maxZoom) google.maps.event.addListenerOnce(this.#map, "idle", () => {
      if (this.#map.getZoom() > maxZoom) this.#map.setZoom(maxZoom);
    });
  }

  #open(entry) {
    if (!entry.marker || !this.#info) return;
    const box = document.createElement("div");
    box.className = "text-sm leading-snug";
    const lines = [...entry.item.querySelectorAll(".text-sm > span:not([data-locator-distance]), .text-sm > a[href^='tel:']")].map((node) => node.textContent);
    const title = document.createElement("strong");
    title.textContent = entry.item.dataset.name;
    box.append(title);
    lines.forEach((line) => {
      const row = document.createElement("div");
      row.textContent = line;
      box.append(row);
    });
    this.#info.setContent(box);
    this.#info.open({ map: this.#map, anchor: entry.marker });
  }

  #miles(a, b) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 3958.8 * 2 * Math.asin(Math.sqrt(h));
  }
}

if (!customElements.get("s-service-locator")) customElements.define("s-service-locator", SServiceLocator);
